import { useState, useEffect, useCallback } from "react";
import { MainLayout } from "../../components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Headphones, Plus, Send, ArrowLeft, Clock, CheckCircle2, MessageCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

const STATUS_MAP = {
  open: { label: "Aberto", color: "bg-blue-100 text-blue-700", icon: AlertCircle },
  waiting: { label: "Aguardando", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  answered: { label: "Respondido", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  closed: { label: "Fechado", color: "bg-gray-100 text-gray-500", icon: CheckCircle2 },
};

const CATEGORY_MAP = {
  general: "Geral",
  technical: "Tecnico",
  billing: "Cobranca",
  feature: "Funcionalidade",
  bug: "Problema/Bug",
};

const PRIORITY_MAP = {
  low: { label: "Baixa", color: "bg-gray-100 text-gray-600" },
  normal: { label: "Normal", color: "bg-blue-100 text-blue-600" },
  high: { label: "Alta", color: "bg-orange-100 text-orange-600" },
  urgent: { label: "Urgente", color: "bg-red-100 text-red-600" },
};

function TicketList({ tickets, onSelect, onNew }) {
  return (
    <div data-testid="ticket-list">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Tickets de Suporte</h2>
        <Button onClick={onNew} size="sm" data-testid="new-ticket-btn">
          <Plus className="w-4 h-4 mr-1" /> Novo Ticket
        </Button>
      </div>

      {tickets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Headphones className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Nenhum ticket de suporte</p>
            <Button onClick={onNew} variant="outline" className="mt-3" data-testid="empty-new-ticket-btn">
              Abrir primeiro ticket
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => {
            const st = STATUS_MAP[t.status] || STATUS_MAP.open;
            const pr = PRIORITY_MAP[t.priority] || PRIORITY_MAP.normal;
            const lastMsg = t.messages?.[t.messages.length - 1];
            return (
              <Card
                key={t.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onSelect(t)}
                data-testid={`ticket-${t.id}`}
              >
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm truncate">{t.subject}</span>
                        <Badge variant="outline" className={`text-[10px] ${st.color}`}>{st.label}</Badge>
                        <Badge variant="outline" className={`text-[10px] ${pr.color}`}>{pr.label}</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{t.ticket_number}</span>
                        <span>-</span>
                        <span>{t.restaurant_name || "Restaurante"}</span>
                        {t.category && <span>- {CATEGORY_MAP[t.category] || t.category}</span>}
                      </div>
                      {lastMsg && (
                        <p className="text-xs text-gray-400 mt-1 truncate">
                          {lastMsg.sender === "admin" ? "Admin" : lastMsg.sender_name}: {lastMsg.message}
                        </p>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-400 whitespace-nowrap">
                      {new Date(t.updated_at).toLocaleDateString("pt-BR")}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TicketDetail({ ticket, onBack, onReply, onStatusChange, isAdmin }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const st = STATUS_MAP[ticket.status] || STATUS_MAP.open;

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    await onReply(ticket.id, reply);
    setReply("");
    setSending(false);
  };

  return (
    <div data-testid="ticket-detail">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="ticket-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-lg font-semibold">{ticket.subject}</h2>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>{ticket.ticket_number}</span>
            <Badge variant="outline" className={`text-[10px] ${st.color}`}>{st.label}</Badge>
            <span>{ticket.restaurant_name}</span>
          </div>
        </div>
        {isAdmin && ticket.status !== "closed" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onStatusChange(ticket.id, "closed")}
            data-testid="close-ticket-btn"
          >
            Fechar Ticket
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[400px]">
            <div className="p-4 space-y-3">
              {ticket.messages?.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === "admin" ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`max-w-[75%] rounded-lg p-3 ${
                      msg.sender === "admin"
                        ? "bg-emerald-50 border border-emerald-200"
                        : "bg-gray-50 border border-gray-200"
                    }`}
                    data-testid={`message-${msg.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium">
                        {msg.sender === "admin" ? "Suporte Kaiso" : msg.sender_name}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {new Date(msg.timestamp).toLocaleString("pt-BR")}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {ticket.status !== "closed" && (
            <div className="border-t p-3 flex gap-2">
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Escreva sua resposta..."
                className="min-h-[60px] resize-none"
                data-testid="reply-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <Button
                onClick={handleSend}
                disabled={!reply.trim() || sending}
                className="self-end"
                data-testid="send-reply-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState("all");
  const [newTicket, setNewTicket] = useState({ subject: "", message: "", category: "general", priority: "normal" });
  const [creating, setCreating] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchTickets = useCallback(async () => {
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await axios.get(`${API}/api/v1/support/tickets${params}`, { headers });
      setTickets(res.data);
    } catch {
      // silently fail
    }
  }, [filter]);

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 15000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  const createTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error("Preencha o assunto e a mensagem");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/api/v1/support/tickets`, newTicket, { headers });
      toast.success("Ticket criado com sucesso!");
      setShowNew(false);
      setNewTicket({ subject: "", message: "", category: "general", priority: "normal" });
      fetchTickets();
    } catch {
      toast.error("Erro ao criar ticket");
    }
    setCreating(false);
  };

  const replyTicket = async (ticketId, message) => {
    try {
      await axios.post(`${API}/api/v1/support/tickets/${ticketId}/reply`, { message }, { headers });
      toast.success("Resposta enviada!");
      const res = await axios.get(`${API}/api/v1/support/tickets/${ticketId}`, { headers });
      setSelected(res.data);
      fetchTickets();
    } catch {
      toast.error("Erro ao enviar resposta");
    }
  };

  const changeStatus = async (ticketId, status) => {
    try {
      await axios.patch(`${API}/api/v1/support/tickets/${ticketId}`, { status }, { headers });
      toast.success("Status atualizado!");
      setSelected(null);
      fetchTickets();
    } catch {
      toast.error("Erro ao atualizar status");
    }
  };

  return (
    <MainLayout title="Suporte">
      <div className="max-w-4xl mx-auto p-4" data-testid="support-page">

        <div className="flex gap-2 mb-4">
          {["all", "open", "waiting", "answered", "closed"].map((f) => (
            <Button
              key={f}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
            >
              {f === "all" ? "Todos" : STATUS_MAP[f]?.label || f}
            </Button>
          ))}
        </div>

        {selected ? (
          <TicketDetail
            ticket={selected}
            onBack={() => setSelected(null)}
            onReply={replyTicket}
            onStatusChange={changeStatus}
          />
        ) : (
          <TicketList
            tickets={tickets}
            onSelect={setSelected}
            onNew={() => setShowNew(true)}
          />
        )}

        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" /> Novo Ticket de Suporte
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                placeholder="Assunto"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                data-testid="new-ticket-subject"
              />
              <div className="grid grid-cols-2 gap-2">
                <Select value={newTicket.category} onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}>
                  <SelectTrigger data-testid="new-ticket-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_MAP).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={newTicket.priority} onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}>
                  <SelectTrigger data-testid="new-ticket-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_MAP).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                placeholder="Descreva seu problema ou duvida..."
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                className="min-h-[120px]"
                data-testid="new-ticket-message"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button onClick={createTicket} disabled={creating} data-testid="submit-ticket-btn">
                {creating ? "Enviando..." : "Enviar Ticket"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
